package com.patronat.top.features.dashboard.presentation.viewmodel

import com.patronat.top.core.base.BaseViewModel
import com.patronat.top.features.usermanagement.domain.usecase.command.GetCurrentUserCommand
import com.patronat.top.features.usermanagement.domain.usecase.GetCurrentUserUseCase
import com.patronat.top.features.dashboard.domain.usecase.GetDashboardStatsUseCase
import com.patronat.top.features.dashboard.domain.usecase.command.GetDashboardStatsCommand
import com.patronat.top.features.dashboard.presentation.contract.DashboardContract.Effect
import com.patronat.top.features.dashboard.presentation.contract.DashboardContract.Event
import com.patronat.top.features.dashboard.presentation.contract.DashboardContract.State
import com.patronat.top.core.navigation.DashboardSection
import com.patronat.top.core.presentation.AlertController
import com.patronat.top.core.presentation.NotificationType
import com.patronat.top.core.utils.UiText
import com.patronat.top.error.AppErrorCode
import io.github.aakira.napier.Napier
import top.design_system.generated.resources.Res as DesignRes
import top.design_system.generated.resources.*

class DashboardViewModel(
    private val alertController: AlertController,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val getDashboardStatsUseCase: GetDashboardStatsUseCase
) : BaseViewModel<State, Event, Effect>(initialState = State()) {

    companion object {
        private const val TAG = "DashboardViewModel"
    }
    init {
        loadProfile()
    }

    override fun onEvent(event: Event) {
        when (event) {
            is Event.OnMenuSelected -> {
                updateState { it.copy(currentSection = event.section) }
            }
            Event.OnRetryClick -> {
                loadProfile()
            }
        }
    }

    private fun loadProfile() {
        launchNetwork(
            request = { getCurrentUserUseCase.execute(GetCurrentUserCommand()) },
            onLoading = { isLoading ->
                updateState { it.copy(isLoading = isLoading) }
            },
            onSuccess = { user ->
                val availableSections = DashboardSection.getAvailableSections(user.role)

                val sectionToSelect = if (currentState.currentSection in availableSections) {
                    currentState.currentSection
                } else {
                    availableSections.firstOrNull() ?: DashboardSection.DASHBOARD
                }

                updateState {
                    it.copy(
                        user = user,
                        currentSection = sectionToSelect
                    )
                }
                loadDashboardStats()
            },
            onError = { type, message ->
                when (type) {
                    AppErrorCode.UNAUTHORIZED -> sendEffect(Effect.NavigateToAuth)
                    else -> handleError(UiText.DynamicString(message ?: "Помилка завантаження профілю"))
                }
            }
        )
    }

    private fun loadDashboardStats() {
        launchNetwork(
            request = { getDashboardStatsUseCase.execute(GetDashboardStatsCommand()) },
            onLoading = { isLoading ->
                updateState { it.copy(isStatsLoading = isLoading) }
            },
            onSuccess = { stats ->
                updateState { it.copy(dashboardStats = stats) }
            },
            onError = { type, message ->
                when (type) {
                    AppErrorCode.UNAUTHORIZED -> sendEffect(Effect.NavigateToAuth)
                    else -> handleError(UiText.DynamicString(message ?: "Помилка завантаження статистики"))
                }
            }
        )
    }

    private fun handleError(message: UiText) {
        Napier.e(tag = TAG) { "Show Alert Error: $message" }
        alertController.show(
            title = UiText.StringResource(DesignRes.string.error_alert_title),
            message = message,
            type = NotificationType.ERROR
        )
    }
}
